import {
  CheckCircle2,
  ChevronDown,
  ChevronRight,
  HelpCircle,
  Loader2,
  Phone,
  PhoneForwarded,
  PhoneOff,
  XCircle,
} from 'lucide-react';
import * as React from 'react';
import { toast } from 'sonner';

import { Button } from '@/components/ui/button';
import {
  Collapsible,
  CollapsibleContent,
  CollapsibleTrigger,
} from '@/components/ui/collapsible';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import {
  Select,
  SelectContent,
  SelectGroup,
  SelectItem,
  SelectLabel,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import {
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from '@/components/ui/tooltip';
import {
  formatCoverageCategory,
  InsuranceContact,
  useInsuranceContacts,
} from '@/hooks/useInsuranceContacts';
import {
  AIHoldAndTransferStatus,
  useCancelAiHoldAndTransferMutation,
  useGetAiHoldAndTransferStatusQuery,
  useGetUserTransferPhoneQuery,
  useListAiHoldAndTransferQuery,
  useStartAiHoldAndTransferMutation,
} from '@/services/api/aiHoldAndTransferService';
import { Task } from '@/services/types/client-intake-types';
import { isPhoneNumberValid, sanitizePhoneNumber } from '@/utils/validation';

const CUSTOM_OPTION = '__CUSTOM__';
const NONE_OPTION = '__NONE__';

const formatPhoneNumber = (phone: string) => {
  if (!phone) return '';
  const digits = phone.replace(/\D/g, '');

  if (digits.length === 11 && digits.startsWith('1')) {
    return `+1 (${digits.slice(1, 4)}) ${digits.slice(4, 7)}-${digits.slice(7)}`;
  }

  if (digits.length === 10) {
    return `(${digits.slice(0, 3)}) ${digits.slice(3, 6)}-${digits.slice(6)}`;
  }

  return phone;
};

interface AIHoldAndTransferCardProps {
  task: Task;
  matterId: string;
}

/**
 * Compact status badge for workflow status.
 */
const StatusBadge: React.FC<{
  status: string;
  transferStatus: AIHoldAndTransferStatus;
}> = ({ status, transferStatus }) => {
  const getStatusConfig = () => {
    if (status === 'completed' || status === 'failed') {
      return {
        icon: status === 'completed' ? CheckCircle2 : XCircle,
        text: status === 'completed' ? 'Done' : 'Failed',
        className:
          status === 'completed'
            ? 'bg-green-100 text-green-800'
            : 'bg-red-100 text-red-800',
      };
    }

    switch (transferStatus) {
      case AIHoldAndTransferStatus.TRANSFER_SUCCESSFUL:
        return {
          icon: CheckCircle2,
          text: 'Transferred',
          className: 'bg-green-100 text-green-800',
        };
      case AIHoldAndTransferStatus.TRANSFER_INITIATED:
        return {
          icon: PhoneForwarded,
          text: 'Transferring',
          className: 'bg-blue-100 text-blue-800 animate-pulse',
        };
      case AIHoldAndTransferStatus.TRANSFER_FAILED:
        return {
          icon: XCircle,
          text: 'Failed',
          className: 'bg-red-100 text-red-800',
        };
      case AIHoldAndTransferStatus.CALL_ENDED:
        return {
          icon: PhoneOff,
          text: 'Ended',
          className: 'bg-gray-100 text-gray-800',
        };
      default:
        return {
          icon: Loader2,
          text: 'On Hold',
          className: 'bg-violet-100 text-violet-800',
        };
    }
  };

  const config = getStatusConfig();
  const Icon = config.icon;

  return (
    <span
      className={`inline-flex items-center gap-1 rounded-full px-2 py-0.5 text-xs font-medium ${config.className}`}
    >
      <Icon
        className={`h-3 w-3 ${transferStatus === AIHoldAndTransferStatus.PENDING ? 'animate-spin' : ''}`}
      />
      {config.text}
    </span>
  );
};

/**
 * AI Hold & Transfer card - collapsible by default.
 * Allows users to start an AI call that waits on hold and transfers when a human answers.
 */
export const AIHoldAndTransferCard: React.FC<AIHoldAndTransferCardProps> = ({
  task,
  matterId,
}) => {
  const [isOpen, setIsOpen] = React.useState(false);

  // Get coverage ID from task
  const coverageId =
    task.coverage_id || (task.coverage?.id as string | undefined);

  const { carrierContacts: allCarrierContacts, adjusterContacts: allAdjusterContacts, isLoading } =
    useInsuranceContacts(matterId);

  // Only show contacts tied to the linked coverage (Bug #4)
  const carrierContacts = React.useMemo(
    () => allCarrierContacts.filter((c) => c.coverageId === coverageId),
    [allCarrierContacts, coverageId],
  );
  const adjusterContacts = React.useMemo(
    () => allAdjusterContacts.filter((c) => c.coverageId === coverageId),
    [allAdjusterContacts, coverageId],
  );

  // Check if user has a Dialpad phone
  const { data: userTransferPhone } = useGetUserTransferPhoneQuery();

  // List active workflows for this matter
  const { data: activeWorkflows } = useListAiHoldAndTransferQuery(
    { matterId },
    {
      pollingInterval: 3000,
      skip: !matterId,
    },
  );

  // Find if there's an active workflow for this coverage
  const activeWorkflowForCoverage = React.useMemo(() => {
    if (!activeWorkflows?.workflows || !coverageId) return null;
    return activeWorkflows.workflows.find((w) => w.coverage_id === coverageId);
  }, [activeWorkflows, coverageId]);

  // Auto-expand when there's an active call
  React.useEffect(() => {
    if (activeWorkflowForCoverage) {
      setIsOpen(true);
    }
  }, [activeWorkflowForCoverage]);

  // Get detailed status for active workflow
  const { data: workflowStatus } = useGetAiHoldAndTransferStatusQuery(
    { workflowId: activeWorkflowForCoverage?.workflow_id || '' },
    {
      skip: !activeWorkflowForCoverage,
      pollingInterval: 3000,
    },
  );

  const [startCall, { isLoading: isStarting }] =
    useStartAiHoldAndTransferMutation();
  const [cancelCall, { isLoading: isCancelling }] =
    useCancelAiHoldAndTransferMutation();

  const [selectValue, setSelectValue] = React.useState<string>(NONE_OPTION);
  const [customValue, setCustomValue] = React.useState('');
  const [showCustomInput, setShowCustomInput] = React.useState(false);
  const [transferToPhone, setTransferToPhone] = React.useState('');

  // Compute selected label for the trigger (Bug #2)
  const allDisplayContacts = React.useMemo(
    () => [...adjusterContacts, ...carrierContacts],
    [adjusterContacts, carrierContacts],
  );
  const selectedLabel = React.useMemo(() => {
    const contact = allDisplayContacts.find((c) => c.phone === selectValue);
    if (contact) {
      const categoryLabel = formatCoverageCategory(contact.coverageCategory);
      return `${contact.name}${categoryLabel ? ` (${categoryLabel})` : ''} – ${formatPhoneNumber(contact.phone)}`;
    }
    if (!showCustomInput && selectValue && selectValue !== NONE_OPTION) {
      return `Custom: ${formatPhoneNumber(selectValue)}`;
    }
    return '';
  }, [allDisplayContacts, selectValue, showCustomInput]);

  // Bug #3: Prefill with user's Dialpad/known transfer number if available
  React.useEffect(() => {
    if (userTransferPhone?.phone_number && !transferToPhone) {
      setTransferToPhone(userTransferPhone.phone_number);
    }
  }, [userTransferPhone, transferToPhone]);

  // Find suggested contact based on task's coverage
  const suggestedContact = React.useMemo(() => {
    if (coverageId) {
      const adjuster = adjusterContacts.find((c) => c.coverageId === coverageId);
      if (adjuster) return adjuster;
      const carrier = carrierContacts.find((c) => c.coverageId === coverageId);
      if (carrier) return carrier;
    }
    return adjusterContacts[0] || carrierContacts[0] || null;
  }, [coverageId, adjusterContacts, carrierContacts]);

  // Initialize with suggested contact
  React.useEffect(() => {
    if (suggestedContact && selectValue === NONE_OPTION) {
      setSelectValue(suggestedContact.phone);
    }
  }, [suggestedContact, selectValue]);

  const handleSelectChange = React.useCallback((value: string) => {
    if (value === CUSTOM_OPTION) {
      setShowCustomInput(true);
      setCustomValue('');
      return;
    }
    setShowCustomInput(false);
    setCustomValue('');
    setSelectValue(value);
  }, []);

  const handleSaveCustom = React.useCallback(() => {
    const trimmed = customValue.trim();
    if (!trimmed || !isPhoneNumberValid(trimmed)) return;
    setSelectValue(trimmed);
    setShowCustomInput(false);
    setCustomValue('');
  }, [customValue]);

  const needsTransferPhone = !userTransferPhone?.has_phone;

  const handleStartCall = React.useCallback(async () => {
    const phoneNumber = selectValue;
    if (!phoneNumber || phoneNumber === NONE_OPTION) {
      toast.error('Please select a carrier phone number');
      return;
    }

    if (!coverageId) {
      toast.error('This task must be linked to a coverage');
      return;
    }

    // Validate transfer phone if user doesn't have Dialpad
    if (needsTransferPhone) {
      if (!transferToPhone.trim()) {
        toast.error('Please enter your phone number for the transfer');
        return;
      }
      if (!isPhoneNumberValid(transferToPhone.trim())) {
        toast.error('Please enter a valid phone number for the transfer');
        return;
      }
    }

    try {
      const e164Phone = sanitizePhoneNumber(phoneNumber);
      const transferTo = needsTransferPhone
        ? sanitizePhoneNumber(transferToPhone)
        : undefined;

      await startCall({
        startAiHoldAndTransferRequest: {
          task_id: task.id,
          phone_number: e164Phone,
          transfer_to: transferTo,
          coverage_id: coverageId,
          matter_id: matterId,
        },
      }).unwrap();

      toast.success("AI Hold & Transfer started. You'll get a Slack notification when ready.");
    } catch (error: unknown) {
      const err = error as {
        status?: number;
        data?: { error?: string | Array<{ msg?: string }> };
      };
      if (err?.status === 409) {
        toast.error('A call is already in progress for this coverage');
      } else {
        let errorMessage = 'Failed to start call. Please try again.';
        if (err?.data?.error) {
          if (typeof err.data.error === 'string') {
            errorMessage = err.data.error;
          } else if (Array.isArray(err.data.error) && err.data.error[0]?.msg) {
            errorMessage = err.data.error[0].msg;
          }
        }
        toast.error(errorMessage);
      }
    }
  }, [selectValue, coverageId, matterId, startCall, needsTransferPhone, transferToPhone]);

  const handleCancelCall = React.useCallback(async () => {
    if (!activeWorkflowForCoverage?.workflow_id) return;
    try {
      await cancelCall({ workflowId: activeWorkflowForCoverage.workflow_id }).unwrap();
      toast.success('Call cancelled');
    } catch {
      toast.error('Failed to cancel call');
    }
  }, [activeWorkflowForCoverage, cancelCall]);

  const hasContacts =
    carrierContacts.length > 0 ||
    adjusterContacts.length > 0;

  const renderContactItem = (contact: InsuranceContact) => {
    const categoryLabel = formatCoverageCategory(contact.coverageCategory);
    return (
      <SelectItem key={contact.id} value={contact.phone}>
        <div className="flex flex-col">
          <span className="text-sm font-medium text-gray-900">
            {contact.name}
            {categoryLabel && (
              <span className="ml-1.5 text-[10px] font-semibold bg-gray-100 text-gray-700 px-1 rounded">
                {categoryLabel}
              </span>
            )}
          </span>
          <span className="text-xs text-gray-500">{formatPhoneNumber(contact.phone)}</span>
        </div>
      </SelectItem>
    );
  };

  // If no coverage, don't show at all
  if (!coverageId) {
    return null;
  }

  // Render active call status (always expanded when active)
  if (activeWorkflowForCoverage) {
    const status = workflowStatus || activeWorkflowForCoverage;
    const isActive = status.status !== 'completed' && status.status !== 'failed';

    return (
      <div className="py-2">
        <div className="border border-violet-200 rounded-lg bg-violet-50/50 p-3 space-y-2">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-2">
              <PhoneForwarded className="h-4 w-4 text-violet-600" />
              <span className="text-sm font-medium text-gray-900">AI Hold & Transfer</span>
            </div>
            <StatusBadge status={status.status} transferStatus={status.transfer_status} />
          </div>

          <p className="text-xs text-violet-700">
            {status.transfer_status === AIHoldAndTransferStatus.PENDING &&
              "AI is on hold. You'll get a Slack notification when a human answers."}
            {status.transfer_status === AIHoldAndTransferStatus.TRANSFER_INITIATED &&
              'Human answered! Transferring to your phone now...'}
            {status.transfer_status === AIHoldAndTransferStatus.TRANSFER_SUCCESSFUL &&
              'Call transferred successfully.'}
            {status.transfer_status === AIHoldAndTransferStatus.TRANSFER_FAILED &&
              'Transfer failed. Please try again.'}
          </p>

          {isActive && (
            <Button
              variant="outline"
              size="sm"
              onClick={handleCancelCall}
              disabled={isCancelling}
              className="w-full text-red-600 border-red-200 hover:bg-red-50 h-8"
            >
              {isCancelling ? (
                <Loader2 className="h-3 w-3 mr-1.5 animate-spin" />
              ) : (
                <PhoneOff className="h-3 w-3 mr-1.5" />
              )}
              Cancel Call
            </Button>
          )}
        </div>
      </div>
    );
  }

  // Collapsed trigger with content
  return (
    <div className="px-2 py-2">
      <Collapsible open={isOpen} onOpenChange={setIsOpen}>
        <CollapsibleTrigger className="flex items-center justify-between w-full py-2 px-3 rounded-lg border border-gray-200 hover:bg-gray-50 transition-colors">
          <div className="flex items-center gap-2">
            <PhoneForwarded className="h-4 w-4 text-violet-600" />
            <span className="text-xs font-medium text-gray-900">AI Hold & Transfer</span>
            <Tooltip>
              <TooltipTrigger asChild>
                <button
                  type="button"
                  className="text-gray-400 hover:text-gray-600"
                  onClick={(e) => e.stopPropagation()}
                >
                  <HelpCircle className="h-3.5 w-3.5" />
                </button>
              </TooltipTrigger>
              <TooltipContent side="top" className="max-w-xs">
                <p className="text-xs">
                  AI calls the carrier, navigates menus, and waits on hold.
                  You&apos;ll get a Slack notification when a human answers.
                </p>
              </TooltipContent>
            </Tooltip>
          </div>
          {isOpen ? (
            <ChevronDown className="h-4 w-4 text-gray-400" />
          ) : (
            <ChevronRight className="h-4 w-4 text-gray-400" />
          )}
        </CollapsibleTrigger>

        <CollapsibleContent className="mt-2 border border-gray-200 rounded-lg p-3 space-y-3">
          {isLoading ? (
            <div className="h-10 bg-gray-100 rounded animate-pulse" />
          ) : !hasContacts ? (
            <div className="space-y-3">
              {selectValue === NONE_OPTION ? (
                <div className="space-y-2">
                  <p className="text-xs text-amber-700 bg-amber-50 border border-amber-200 rounded p-2">
                    No carrier contacts found. Enter the carrier phone number manually.
                  </p>
                  <Input
                    type="tel"
                    value={customValue}
                    onChange={(e) => setCustomValue(e.target.value)}
                    placeholder="Carrier phone number"
                    className="h-9"
                  />
                  <Button
                    size="sm"
                    onClick={() => {
                      if (isPhoneNumberValid(customValue.trim())) {
                        setSelectValue(customValue.trim());
                        setCustomValue('');
                      }
                    }}
                    disabled={!isPhoneNumberValid(customValue.trim())}
                    className="h-8"
                  >
                    Use This Number
                  </Button>
                </div>
              ) : (
                <>
                  <div className="flex items-center justify-between">
                    <div className="text-sm text-gray-900">
                      <span className="text-xs text-gray-500">Calling: </span>
                      {formatPhoneNumber(selectValue)}
                    </div>
                    <Button
                      size="sm"
                      variant="ghost"
                      onClick={() => setSelectValue(NONE_OPTION)}
                      className="h-7 text-xs"
                    >
                      Change
                    </Button>
                  </div>

                  {needsTransferPhone ? (
                    <div className="space-y-1.5">
                      <Label className="text-xs text-gray-600">Transfer to (your phone)</Label>
                      <Input
                        type="tel"
                        value={transferToPhone}
                        onChange={(e) => setTransferToPhone(e.target.value)}
                        placeholder="Your phone number"
                        className="h-9"
                      />
                      <p className="text-xs text-gray-500">
                        Enter the phone number where you want to receive the call when a human answers.
                      </p>
                      {transferToPhone && !isPhoneNumberValid(transferToPhone.trim()) && (
                        <p className="text-xs text-red-600">Enter a valid phone number</p>
                      )}
                    </div>
                  ) : (
                    <p className="text-xs text-gray-500 flex items-center gap-1">
                      <Phone className="h-3 w-3" />
                      Will transfer to your RingCentral number when a human answers
                    </p>
                  )}

                  <Button
                    onClick={handleStartCall}
                    disabled={
                      isStarting ||
                      (needsTransferPhone && !isPhoneNumberValid(transferToPhone.trim()))
                    }
                    className="w-full bg-violet-600 text-white hover:bg-violet-700 h-9"
                  >
                    {isStarting ? (
                      <>
                        <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                        Starting...
                      </>
                    ) : (
                      <>
                        <Phone className="h-4 w-4 mr-2" />
                        Start Call
                      </>
                    )}
                  </Button>
                </>
              )}
            </div>
          ) : (
            <>
              {/* Carrier contact selector */}
              <div className="space-y-1.5">
                <Label className="text-xs text-gray-600">Call</Label>
                <Select
                  value={showCustomInput ? CUSTOM_OPTION : selectValue}
                  onValueChange={handleSelectChange}
                >
                  <SelectTrigger className="h-9">
                    <SelectValue placeholder="Select carrier contact">
                      {selectedLabel && (
                        <span className="text-sm">{selectedLabel}</span>
                      )}
                    </SelectValue>
                  </SelectTrigger>
                  <SelectContent className="max-h-60">
                    {adjusterContacts.length > 0 && (
                      <SelectGroup>
                        <SelectLabel className="text-xs">Adjusters</SelectLabel>
                        {adjusterContacts.map(renderContactItem)}
                      </SelectGroup>
                    )}
                    {carrierContacts.length > 0 && (
                      <SelectGroup>
                        <SelectLabel className="text-xs">Carriers</SelectLabel>
                        {carrierContacts.map(renderContactItem)}
                      </SelectGroup>
                    )}
                    <SelectItem value={CUSTOM_OPTION}>Enter custom number</SelectItem>
                  </SelectContent>
                </Select>
              </div>

              {/* Custom phone input */}
              {showCustomInput && (
                <div className="space-y-1.5">
                  <Input
                    type="tel"
                    value={customValue}
                    onChange={(e) => setCustomValue(e.target.value)}
                    placeholder="Enter phone number"
                    className="h-9"
                  />
                  <div className="flex gap-2">
                    <Button size="sm" onClick={handleSaveCustom} disabled={!isPhoneNumberValid(customValue.trim())} className="h-8">
                      Save
                    </Button>
                    <Button size="sm" variant="ghost" onClick={() => setShowCustomInput(false)} className="h-8">
                      Cancel
                    </Button>
                  </div>
                </div>
              )}

              {/* Transfer destination - show Dialpad indicator or phone input */}
              {needsTransferPhone ? (
                <div className="space-y-1.5">
                  <Label className="text-xs text-gray-600">Transfer to (your phone)</Label>
                  <Input
                    type="tel"
                    value={transferToPhone}
                    onChange={(e) => setTransferToPhone(e.target.value)}
                    placeholder="Your phone number"
                    className="h-9"
                  />
                  <p className="text-xs text-gray-500">
                    Enter the phone number where you want to receive the call when a human answers.
                  </p>
                  {transferToPhone && !isPhoneNumberValid(transferToPhone.trim()) && (
                    <p className="text-xs text-red-600">Enter a valid phone number</p>
                  )}
                </div>
              ) : (
                <p className="text-xs text-gray-500 flex items-center gap-1">
                  <Phone className="h-3 w-3" />
                  Will transfer to your RingCentral number when a human answers
                </p>
              )}

              {/* Start button */}
              {selectValue !== NONE_OPTION && !showCustomInput && (
                <Button
                  onClick={handleStartCall}
                  disabled={
                    isStarting ||
                    !selectValue ||
                    selectValue === NONE_OPTION ||
                    (needsTransferPhone && !isPhoneNumberValid(transferToPhone.trim()))
                  }
                  className="w-full bg-violet-600 text-white hover:bg-violet-700 h-9"
                >
                  {isStarting ? (
                    <>
                      <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                      Starting...
                    </>
                  ) : (
                    <>
                      <Phone className="h-4 w-4 mr-2" />
                      Start Call
                    </>
                  )}
                </Button>
              )}
            </>
          )}
        </CollapsibleContent>
      </Collapsible>
    </div>
  );
};
